// SPDX-License-Identifier: Elastic-2.0
/**
 * OnboardingPage — orchestrator for the 6-step wizard.
 *
 * History:
 *  - T-013 created the placeholder + 4 s A6 auto-advance.
 *  - T-019 replaced A6 with the real 5-phase magic moment that owns its
 *    own manual "Llevame al panel →" CTA. The auto-advance was removed
 *    by the Group 4 orchestrator integration to avoid racing the CTA.
 *  - T-018 introduced the shared A5 skip hook so the body's skip button
 *    and the WizardShell footer's secondary "Skip" button converge.
 *  - F-348 reframed that path: skip now records **account-less Free**
 *    (anonymous device_id, no token, no account) via
 *    `useA5SkipAccountless`, replacing the former local-JWT issuance.
 *
 * Responsibilities:
 *  - Read `step` from `useOnboardingStore` (persisted; refresh restores)
 *  - Render the matching `<WizardShell>` with the active step body
 *  - Decide button labels: "Continue" everywhere except A5 → A6 where
 *    the primary becomes "Start"
 *  - On A5, show the "Skip" secondary that runs the shared account-less
 *    flow and advances
 *
 * What this component intentionally does NOT do:
 *  - Per-step input rendering — that's the step body's job
 *  - Theme switching — `ThemePill` (inside WizardShell) wires itself
 *    directly to `useTheme`
 *  - i18n locale switching — `A1Language` (T-014) does that
 *  - Onboarding completion / navigation — A6's manual CTA owns it
 */
import { Suspense } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";

import { STEPS, TOTAL_STEPS } from "./onboardingSteps";
import { useA5SkipAccountless } from "./steps/useA5SkipAccountless";
import { useOnboardingStore } from "./useOnboardingStore";
import { WizardShell } from "./WizardShell";

/**
 * Suspense fallback used while a lazy step body's JS chunk is in flight.
 * Intentionally invisible — the chrome stays mounted, so a brief blank
 * content area reads as "loading…" without being ugly. We keep the same
 * size as the placeholders (single line) so layout doesn't jump.
 */
function StepLoading(): JSX.Element {
  return <div aria-hidden="true" style={{ minHeight: "1em" }} />;
}

export function OnboardingPage(): JSX.Element {
  const { t } = useTranslation();

  const step = useOnboardingStore((s) => s.step);
  const language = useOnboardingStore((s) => s.language);
  const network = useOnboardingStore((s) => s.network);
  const location = useOnboardingStore((s) => s.location);
  const privacy = useOnboardingStore((s) => s.privacy);
  const auth = useOnboardingStore((s) => s.auth);
  const setStep = useOnboardingStore((s) => s.setStep);

  // Shared account-less flow (F-348) consumed by both the A5 body button
  // and the wizard footer's secondary "Skip" — keeps both code paths
  // converged on the same store mutation (device_id, no token/account).
  const a5Skip = useA5SkipAccountless();

  // Re-assemble the persisted slice from individual selectors. Each
  // primitive selector is reference-stable across renders, so this
  // avoids the "new object per render" infinite-loop trap that selecting
  // a literal would create.
  const persistedSlice = { step, language, network, location, privacy, auth };

  // Defensive clamping: if a persisted `step` ever drifts out of range
  // (e.g. STEPS array shrinks in a future release), snap to the last
  // valid step rather than crashing.
  const safeStep = Math.min(Math.max(0, Math.floor(step)), TOTAL_STEPS - 1);
  const config = STEPS[safeStep]!;
  const isLastStep = safeStep === TOTAL_STEPS - 1;
  const Body = config.Body;

  const handleNext = (): void => {
    if (safeStep < TOTAL_STEPS - 1) {
      setStep(safeStep + 1);
    }
  };

  const handleBack = (): void => {
    if (safeStep > 0) {
      setStep(safeStep - 1);
    }
  };

  const handleSkipLocal = async (): Promise<void> => {
    // A5 "Skip — set up later" → run the shared account-less flow (F-348:
    // loads the device_id, records account-less Free in useAuth +
    // onboarding store; no token, no account). On success advance to A6;
    // on failure the hook surfaces its own error.
    const ok = await a5Skip.runSkip();
    if (ok) {
      setStep(safeStep + 1);
    }
  };

  // Next-button label: "Start" when leaving A5 (i.e. on A5 itself,
  // because clicking Next there crosses into A6); "Continue" otherwise.
  // A6 hides the footer anyway so the label never renders for it.
  const nextLabel = config.id === "A5"
    ? t("onboarding.start")
    : t("onboarding.continue");

  // Secondary control: A5 "Skip — set up later" is the only step that
  // surfaces one. Disabled while the device_id load is in flight.
  const secondary = config.hasSecondarySkip ? (
    <Button
      variant="secondary"
      onClick={() => {
        void handleSkipLocal();
      }}
      disabled={a5Skip.loading}
      data-testid="wizard-skip-local"
    >
      {t("onboarding.skip")}
    </Button>
  ) : undefined;

  const canNext = !isLastStep && config.canNext(persistedSlice);

  // `data-testid="onboarding-page"` is the stable hook the T-004 router
  // integration tests assert on (see `tests/routing/router.test.tsx`).
  // The inner `wizard-shell` + per-step bodies expose their own ids.
  return (
    <div data-testid="onboarding-page" style={{ display: "contents" }}>
      <WizardShell
        step={safeStep}
        totalSteps={TOTAL_STEPS}
        stepKey={config.id}
        onBack={handleBack}
        onNext={handleNext}
        canNext={canNext}
        nextLabel={nextLabel}
        secondary={secondary}
        hideFooter={config.hideFooter}
      >
        <Suspense fallback={<StepLoading />}>
          <Body />
        </Suspense>
      </WizardShell>
    </div>
  );
}
